import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './SchedulePage.module.css';
import { IoArrowUpOutline, IoCalendarOutline, IoLocationOutline, IoSearchOutline, IoTimeOutline, IoTrophyOutline } from 'react-icons/io5';
import { scheduleMatches, scheduleSummary } from '../data/scheduleData';

const allValue = 'All';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const EASTERN_TIME_OFFSET = '-04:00';
const timeZoneOptions = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
];

function getDefaultTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

function formatDate(value) {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Used by summary cards (scheduleData format)
function getKickoffDate(match) {
  const date = new Date(`${match.date}T${match.kickoffET}:00${EASTERN_TIME_OFFSET}`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatConvertedTime(match, timeZone) {
  const date = getKickoffDate(match);
  if (!date) return match.kickoffET || 'TBD';
  const parts = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone,
  }).formatToParts(date);
  const partMap = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${partMap.hour}:${partMap.minute}`;
}

// Used by table (DB format: match_date + kickoff_time)
function formatDbTime(matchDate, kickoffTime, timeZone) {
  if (!matchDate || !kickoffTime) return 'TBD';
  const dateStr = matchDate.slice(0, 10);
  const date = new Date(`${dateStr}T${kickoffTime}:00${EASTERN_TIME_OFFSET}`);
  if (Number.isNaN(date.getTime())) return kickoffTime;
  const parts = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone,
  }).formatToParts(date);
  const partMap = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${partMap.hour}:${partMap.minute}`;
}

function buildResult(row) {
  if (row.home_score === null || row.away_score === null) return '';
  let result = `${row.home_team} ${row.home_score}-${row.away_score} ${row.away_team}`;
  if (row.penalty_winner) {
    result += ` (${row.penalty_winner} won on penalties)`;
  }
  return result;
}

function SchedulePage() {
  const schedulePanelRef = useRef(null);
  const [localTimeZone] = useState(getDefaultTimeZone);
  const timeZones = useMemo(() => {
    return timeZoneOptions.includes(localTimeZone)
      ? timeZoneOptions
      : [localTimeZone, ...timeZoneOptions];
  }, [localTimeZone]);
  const [selectedTimeZone, setSelectedTimeZone] = useState(localTimeZone);
  const [stageFilter, setStageFilter] = useState(allValue);
  const [searchTerm, setSearchTerm] = useState('');
  const [dbMatches, setDbMatches] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchSchedule = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/matches/schedule`);
        const data = await response.json();
        if (isMounted && Array.isArray(data)) {
          setDbMatches(data);
        }
      } catch (err) {
        console.error('Failed to load schedule from DB:', err);
      } finally {
        if (isMounted) setTableLoading(false);
      }
    };
    fetchSchedule();
    return () => { isMounted = false; };
  }, []);

  const stages = useMemo(() => [allValue, ...new Set(scheduleMatches.map((m) => m.stage))], []);

  const filteredMatches = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    return dbMatches.filter((m) => {
      const stageMatches = stageFilter === allValue || m.stage === stageFilter;
      const searchable = [
        m.match_no,
        m.match_date?.slice(0, 10),
        m.stage,
        m.group_name,
        m.home_team,
        m.away_team,
        m.city,
        m.country,
        m.stadium,
        buildResult(m),
        formatDbTime(m.match_date, m.kickoff_time, selectedTimeZone),
      ].filter(Boolean).join(' ').toLowerCase();
      return stageMatches && (!search || searchable.includes(search));
    });
  }, [dbMatches, stageFilter, searchTerm, selectedTimeZone]);

  const firstMatch = scheduleMatches[0];
  const finalMatch = scheduleMatches[scheduleMatches.length - 1];
  const topCities = scheduleSummary.cityCounts.slice(0, 5);
  const scrollToTop = () => {
    schedulePanelRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  return (
    <section className={styles.schedulePage}>
      <div className={styles.hero}>
        <div>
          <p>Complete Match Schedule</p>
          <h2>World Cup 2026 Tracker</h2>
          <span>All kickoff times are Eastern Time (ET)</span>
        </div>
        <IoTrophyOutline />
      </div>

      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <IoCalendarOutline />
          <span>Total Matches</span>
          <strong>{scheduleSummary.totalMatches}</strong>
        </div>
        <div className={styles.summaryCard}>
          <IoTimeOutline />
          <span>Opening Match</span>
          <strong>{formatDate(firstMatch.date)}, {firstMatch.kickoffET}</strong>
          <small>{firstMatch.city}</small>
        </div>
        <div className={styles.summaryCard}>
          <IoTrophyOutline />
          <span>Final</span>
          <strong>{formatDate(finalMatch.date)}, {finalMatch.kickoffET}</strong>
          <small>{finalMatch.stadium}</small>
        </div>
        <div className={styles.summaryCard}>
          <IoLocationOutline />
          <span>Host Cities</span>
          <strong>{scheduleSummary.cityCounts.length}</strong>
        </div>
      </div>

      <div className={styles.distributionPanel}>
        <div>
          <h3>By Stage</h3>
          <div className={styles.stageChips}>
            {scheduleSummary.stageCounts.map((stage) => (
              <span key={stage.stage}>
                {stage.stage}
                <strong>{stage.matches}</strong>
              </span>
            ))}
          </div>
        </div>
        <div>
          <h3>Top Host Cities</h3>
          <div className={styles.cityList}>
            {topCities.map((city) => (
              <span key={city.city}>
                <b>{city.city}</b>
                <small>{city.stadium}</small>
                <strong>{city.matches}</strong>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.schedulePanel} ref={schedulePanelRef}>
        <div className={styles.toolbar}>
          <label className={styles.searchBox}>
            <IoSearchOutline />
            <input
              type="search"
              value={searchTerm}
              placeholder="Search team, city, stadium..."
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </label>
          <select value={selectedTimeZone} onChange={(event) => setSelectedTimeZone(event.target.value)} aria-label="Time Zone">
            {timeZones.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
          <select value={stageFilter} onChange={(event) => setStageFilter(event.target.value)} aria-label="Stage">
            {stages.map((stage) => <option key={stage} value={stage}>{stage === allValue ? 'Select Stage' : stage}</option>)}
          </select>
          <span>{filteredMatches.length} matches</span>
          {tableLoading && <small className={styles.syncMuted}>Loading...</small>}
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.scheduleTable}>
            <thead>
              <tr>
                <th>No.</th>
                <th>Date</th>
                <th>Time</th>
                <th>Stage</th>
                <th>Group</th>
                <th>Match</th>
                <th>Venue</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {filteredMatches.map((m) => {
                const result = buildResult(m);
                const dateStr = m.match_date?.slice(0, 10);
                return (
                  <tr key={m.match_no}>
                    <td><b>{m.match_no}</b></td>
                    <td><strong>{dateStr ? formatDate(dateStr) : '-'}</strong></td>
                    <td>{formatDbTime(m.match_date, m.kickoff_time, selectedTimeZone)}</td>
                    <td><em>{m.stage}</em></td>
                    <td>{m.group_name || '-'}</td>
                    <td>
                      <strong>{m.home_team}</strong>
                      <span>vs</span>
                      <strong>{m.away_team}</strong>
                    </td>
                    <td><strong>{m.stadium}</strong><span>{m.city}, {m.country}</span></td>
                    <td>
                      <strong className={result ? styles.resultValue : styles.emptyResult}>
                        {result || '-'}
                      </strong>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <button type="button" className={styles.backToTop} onClick={scrollToTop}>
        <IoArrowUpOutline />
        Back to top
      </button>
    </section>
  );
}

export default SchedulePage;
